import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Popup from "../../../components/Popup";
import {
  ActionsCell,
  Badge,
  ClickableRow,
  ContentTypeBadge,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  Segmented,
  StatusBadge,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import { deleteGeneralContent, getMovieList } from "../../../services/content";
import { formatDuration } from "../../../utils/format";
import { capitalize, matches, newestFirst } from "../../../utils/list";
import { canEditContent } from "../../../utils/roles";
import { repairMediaDuration } from "../catalogue/mediaUploads";
import TrendingToggle from "./TrendingToggle";

// The site only lists active videos marked "released": an active video still
// "upcoming" stays hidden from visitors.
const onSite = (movie) => movie?.status === true && movie?.availability === "released";

const matchesStatus = (movie, status) => {
  if (status === "all") return true;
  if (status === "inactive") return movie?.status !== true;
  if (status === "online") return onSite(movie);
  if (status === "trending") return movie?.isTrending === true;
  if (status === "scheduled") return Boolean(movie?.scheduledReleaseAt) && !onSite(movie);
  return movie?.status === true && !onSite(movie);
};

// Last list loaded: shown at once when coming back to the page, then refreshed
let lastList = null;
// Media whose duration was already asked to JW Player in this session
const durationTried = new Set();

const MovieList = () => {
  const canEdit = canEditContent("videos");
  const history = useHistory();
  const { t } = useTranslation();

  const [movieList, setMovieList] = useState(() => lastList || []);
  const [loading, setLoading] = useState(!lastList);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [format, setFormat] = useState("all");
  const [movieToDelete, setMovieToDelete] = useState(null);

  useEffect(() => {
    let active = true;
    getMovieList().then((result) => {
      if (!active) return;
      const sortedList = newestFirst(result?.allMovies) || [];
      lastList = sortedList;
      setMovieList(sortedList);
      setLoading(false);
      // Videos saved before JW Player computed their duration get it now (once per session each)
      sortedList
        .filter((movie) => movie.media?._id && !(movie.media.duration > 0) && !durationTried.has(movie.media._id))
        .forEach((movie) => {
          durationTried.add(movie.media._id);
          repairMediaDuration(movie.media)
            .then((response) => {
              const updated = response?.data?.updatedMedia;
              if (!updated?.duration || !active) return;
              setMovieList((current) =>
                current.map((item) =>
                  item._id === movie._id
                    ? { ...item, media: { ...item.media, duration: updated.duration } }
                    : item
                )
              );
            })
            .catch((error) => console.error("Error while repairing duration:", error));
        });
    });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return movieList.filter(
      (movie) =>
        matches(movie?.media?.title, search) &&
        matchesStatus(movie, status) &&
        (format === "all" || (format === "short") === (movie?.isShort === true))
    );
  }, [movieList, search, status, format]);
  const pagination = usePagination(filtered, 10, [search, status, format]);
  const infoPath = (movie) => `/dashboard/video-info/${movie?._id}`;

  const handleDelete = async (generalContent_id) => {
    try {
      await deleteGeneralContent(generalContent_id);
      toast.success("Movie Deleted Successfully!", {
        progressStyle: { background: "var(--iq-primary)" },
      });
      const result = await getMovieList();
      lastList = newestFirst(result?.allMovies) || [];
      setMovieList(lastList);
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Page>
      <PageHeader
        title={t("Videos")}
        subtitle={
          loading ? " " : `${movieList.length} ${t("videos in the catalogue")}`
        }
        actions={
          canEdit && (
            <Link to="/dashboard/add-video" className="ui-btn">
              <i className="ri-add-line" aria-hidden="true"></i>
              {t("Add Video")}
            </Link>
          )
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("Search video . . .")}
          />
          <Segmented
            label={t("status")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "all", label: t("All") },
              { value: "online", label: t("On the site") },
              { value: "upcoming", label: t("Upcoming") },
              { value: "inactive", label: t("Inactive") },
              { value: "scheduled", label: t("Scheduled") },
              { value: "trending", label: t("Trending") },
            ]}
          />
          <Segmented
            label={t("Format")}
            value={format}
            onChange={setFormat}
            options={[
              { value: "all", label: t("All") },
              { value: "video", label: t("Videos") },
              { value: "short", label: t("Shorts") },
            ]}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-film-line">{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t("Video")}</th>
                    <th>{t("content type")}</th>
                    <th>{t("genre")}</th>
                    <th>{t("Release Date")}</th>
                    <th>{t("status")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((movie) => {
                    const genres = movie?.genre?.map((g) => g.name).join(", ");
                    return (
                      <ClickableRow key={movie?._id} to={infoPath(movie)}>
                        <td>
                          <div className="ui-media">
                            <img
                              className="ui-media-thumb"
                              src={movie?.thumbnail?.static_thumbnail_url || ""}
                              alt=""
                              loading="lazy"
                            />
                            <div style={{ minWidth: 0 }}>
                              <span className="ui-media-heading">
                                <Link
                                  to={infoPath(movie)}
                                  className="ui-media-title"
                                  title={movie?.media?.title}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {movie?.media?.title}
                                </Link>
                                {movie?.isTrending && (
                                  <Badge color="amber">
                                    <i className="ri-fire-fill" aria-hidden="true"></i>
                                    {t("Trending")}
                                  </Badge>
                                )}
                                {movie?.isShort && (
                                  <Badge color="violet">
                                    <i
                                      className="ri-smartphone-line"
                                      aria-hidden="true"
                                    ></i>
                                    {t("Short")}
                                  </Badge>
                                )}
                              </span>
                              <span className="ui-media-meta">
                                {[
                                  formatDuration(movie?.media?.duration),
                                  capitalize(movie?.media?.default_language),
                                  movie?.media?.monetization
                                    ? t("Monetized")
                                    : t("Not monetized"),
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <ContentTypeBadge type={movie?.content_type} />
                        </td>
                        <td>
                          <span className="ui-clamp" title={genres}>
                            {genres}
                          </span>
                        </td>
                        <td className="ui-cell-muted">
                          {movie?.media?.release_year?.substring(0, 10)}
                        </td>
                        <td>
                          {movie?.scheduledReleaseAt && !onSite(movie) ? (
                            <Badge color="blue" dot>
                              {t("Online on {{date}}", {
                                date: new Date(movie.scheduledReleaseAt).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }),
                              })}
                            </Badge>
                          ) : movie?.status === true && !onSite(movie) ? (
                            <Badge color="amber" dot>
                              {t("Upcoming")}
                            </Badge>
                          ) : (
                            <StatusBadge active={movie?.status === true} />
                          )}
                        </td>
                        <ActionsCell>
                          <IconButton
                            icon="ri-eye-line"
                            label={t("View")}
                            onClick={() => history.push(infoPath(movie))}
                          />
                          {canEdit && (
                            <>
                              <TrendingToggle
                                compact
                                video={movie}
                                onChange={(isTrending) =>
                                  setMovieList((current) =>
                                    current.map((item) =>
                                      item._id === movie._id ? { ...item, isTrending } : item
                                    )
                                  )
                                }
                              />
                              <IconButton
                                icon="ri-pencil-line"
                                label={t("Edit")}
                                onClick={() =>
                                  history.push(
                                    `/dashboard/update-video/${movie?._id}`
                                  )
                                }
                              />
                              <IconButton
                                icon="ri-delete-bin-line"
                                label={t("Delete")}
                                tone="danger"
                                onClick={() => setMovieToDelete(movie)}
                              />
                            </>
                          )}
                        </ActionsCell>
                      </ClickableRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {movieToDelete && (
        <Popup
          open={true}
          setOpen={() => setMovieToDelete(null)}
          deleteHandle={() => handleDelete(movieToDelete._id)}
        >
          <div className="model-wrapper">
            {t("Are you sure, you want to delete")}{" "}
            {movieToDelete?.media?.title}?
          </div>
        </Popup>
      )}
    </Page>
  );
};

export default MovieList;
